import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getBackendUrl, getSyncData, syncOfflineOrders } from '../lib/api';
import { appendSupportLogEntry, readPosDemoMode, writePosDemoMode } from '../pos/settingsRuntime';
import { toast } from 'sonner';

const OfflineContext = createContext();

// IndexedDB helper functions
const DB_NAME = 'fiscalpos_offline';
const DB_VERSION = 1;

const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      ['categories', 'products', 'rooms', 'tables', 'settings', 'pending_orders', 'sync_meta']
        .forEach(name => {
          if (!db.objectStoreNames.contains(name))
            db.createObjectStore(name, { keyPath: 'id' });
        });
    };
  });
};

const saveToStore = async (storeName, data) => {
  const db = await openDB();
  const tx = db.transaction(storeName, 'readwrite');
  const store = tx.objectStore(storeName);

  store.clear();

  if (Array.isArray(data)) {
    data.forEach(item => store.add(item));
  } else {
    store.add(data);
  }

  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

const getFromStore = async (storeName) => {
  const db = await openDB();
  const tx = db.transaction(storeName, 'readonly');
  const store = tx.objectStore(storeName);

  return new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

const addPendingOrder = async (order) => {
  const db = await openDB();
  const tx = db.transaction('pending_orders', 'readwrite');
  tx.objectStore('pending_orders').add(order);

  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

const removePendingOrder = async (orderId) => {
  const db = await openDB();
  const tx = db.transaction('pending_orders', 'readwrite');
  tx.objectStore('pending_orders').delete(orderId);

  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

const getPendingOrders = async () => {
  return getFromStore('pending_orders');
};

const clearPendingOrders = async () => {
  const db = await openDB();
  const tx = db.transaction('pending_orders', 'readwrite');
  tx.objectStore('pending_orders').clear();

  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

// Save a key-value pair into sync_meta store (for SW Background Sync)
const saveSyncMeta = async (id, data) => {
  const db = await openDB();
  const tx = db.transaction('sync_meta', 'readwrite');
  tx.objectStore('sync_meta').put({ id, ...data });
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

export function OfflineProvider({ children }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [demoModeEnabled, setDemoModeEnabledState] = useState(readPosDemoMode());
  const [offlineData, setOfflineData] = useState({
    categories: [],
    products: [],
    rooms: [],
    tables: [],
    settings: null
  });
  const [pendingOrdersCount, setPendingOrdersCount] = useState(0);

  // Refs to avoid stale closures in event handlers
  const isOnlineRef = useRef(navigator.onLine);
  const isSyncingRef = useRef(false);
  const syncDataRef = useRef(null);
  const demoModeEnabledRef = useRef(readPosDemoMode());

  // Monitor online status — use refs so handlers are stable
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      isOnlineRef.current = true;
      if (demoModeEnabledRef.current) {
        toast.info('Verbindung wiederhergestellt. Demo-Modus bleibt aktiv.');
        appendSupportLogEntry({
          type: 'network',
          message: 'Netzwerk wiederhergestellt, automatische Synchronisation wegen Demo-Modus uebersprungen.',
        });
        return;
      }

      toast.success('Verbindung wiederhergestellt');
      appendSupportLogEntry({
        type: 'network',
        message: 'Netzwerkverbindung wiederhergestellt.',
      });
      syncDataRef.current?.();
    };

    const handleOffline = () => {
      setIsOnline(false);
      isOnlineRef.current = false;
      toast.warning('Offline-Modus aktiviert');
      appendSupportLogEntry({
        type: 'network',
        level: 'warning',
        message: 'Offline-Modus aktiviert.',
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []); // stable — no deps needed because we use refs

  useEffect(() => {
    demoModeEnabledRef.current = demoModeEnabled;
  }, [demoModeEnabled]);

  // Listen for SYNC_COMPLETE messages from service worker
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleMessage = (event) => {
      if (event.data?.type === 'SYNC_COMPLETE') {
        const { synced, failed } = event.data;
        if (synced > 0) toast.success(`${synced} Bestellung${synced !== 1 ? 'en' : ''} synchronisiert`);
        if (failed > 0) toast.warning(`${failed} Bestellung${failed !== 1 ? 'en' : ''} konnten nicht synchronisiert werden`);
        // Refresh pending count from IDB
        getPendingOrders().then(orders => setPendingOrdersCount(orders.length));
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => navigator.serviceWorker.removeEventListener('message', handleMessage);
  }, []);

  // Initial data load
  useEffect(() => {
    loadOfflineData();
  }, []);

  const loadOfflineData = async () => {
    try {
      const [categories, products, rooms, tables, settings] = await Promise.all([
        getFromStore('categories'),
        getFromStore('products'),
        getFromStore('rooms'),
        getFromStore('tables'),
        getFromStore('settings')
      ]);

      setOfflineData({
        categories,
        products,
        rooms,
        tables,
        settings: settings[0] || null
      });

      const pending = await getPendingOrders();
      setPendingOrdersCount(pending.length);
    } catch (error) {
      console.error('Error loading offline data:', error);
    }
  };

  const syncData = useCallback(async () => {
    if (!isOnlineRef.current || isSyncingRef.current) return;
    if (demoModeEnabledRef.current) {
      appendSupportLogEntry({
        type: 'sync',
        level: 'warning',
        message: 'Synchronisation uebersprungen, weil Demo-Modus aktiv ist.',
      });
      return { skipped: true, reason: 'demo_mode' };
    }

    isSyncingRef.current = true;
    setIsSyncing(true);

    try {
      // Sync pending orders first
      const pendingOrders = await getPendingOrders();
      if (pendingOrders.length > 0) {
        const result = await syncOfflineOrders(pendingOrders);

        // Delete only successfully synced orders (partial sync recovery)
        const failedIds = new Set((result.failed || []).map(f => f.id));
        for (const order of pendingOrders) {
          if (!failedIds.has(order.id)) {
            await removePendingOrder(order.id);
          }
        }

        const syncedCount = result.synced || 0;
        const failedCount = result.failed?.length || 0;
        setPendingOrdersCount(failedCount);

        if (syncedCount > 0) toast.success(`${syncedCount} Bestellung${syncedCount !== 1 ? 'en' : ''} synchronisiert`);
        if (failedCount > 0) toast.warning(`${failedCount} Bestellung${failedCount !== 1 ? 'en' : ''} konnten nicht synchronisiert werden`);
        appendSupportLogEntry({
          type: 'sync',
          level: failedCount > 0 ? 'warning' : 'info',
          message: failedCount > 0
            ? `Synchronisation mit ${failedCount} Fehlern abgeschlossen.`
            : 'Ausstehende Bestellungen erfolgreich synchronisiert.',
          details: { synced: syncedCount, failed: failedCount },
        });
      }

      // Get fresh data from server
      const data = await getSyncData();

      // Save to IndexedDB
      await Promise.all([
        saveToStore('categories', data.categories),
        saveToStore('products', data.products),
        saveToStore('rooms', data.rooms),
        saveToStore('tables', data.tables),
        saveToStore('settings', [{ ...data.settings, id: 'system_settings' }]),
        saveToStore('sync_meta', [{ id: 'last_sync', time: data.synced_at }])
      ]);

      setOfflineData({
        categories: data.categories,
        products: data.products,
        rooms: data.rooms,
        tables: data.tables,
        settings: data.settings
      });

      setLastSyncTime(new Date(data.synced_at));
      appendSupportLogEntry({
        type: 'sync',
        message: 'Basisdaten aus dem Backend aktualisiert.',
        details: {
          categories: data.categories?.length || 0,
          products: data.products?.length || 0,
          rooms: data.rooms?.length || 0,
          tables: data.tables?.length || 0,
          synced_at: data.synced_at,
        },
      });

    } catch (error) {
      console.error('Sync error:', error);
      appendSupportLogEntry({
        type: 'sync',
        level: 'error',
        message: 'Synchronisation fehlgeschlagen.',
        details: { error: error?.message || 'Unbekannter Fehler' },
      });
      if (isOnlineRef.current) {
        toast.error('Synchronisation fehlgeschlagen');
      }
    } finally {
      isSyncingRef.current = false;
      setIsSyncing(false);
    }
  }, []); // stable — uses refs for online/syncing checks

  // Keep ref current so the stable online handler always calls latest version
  syncDataRef.current = syncData;

  const savePendingOrder = useCallback(async (order) => {
    await addPendingOrder(order);
    setPendingOrdersCount(prev => prev + 1);

    // Persist token + backend URL for the SW Background Sync
    try {
      const token = localStorage.getItem('fiscalpos_token');
      const backendUrl = getBackendUrl();
      await Promise.all([
        token ? saveSyncMeta('auth_token', { token }) : Promise.resolve(),
        backendUrl ? saveSyncMeta('backend_url', { url: backendUrl }) : Promise.resolve(),
      ]);
    } catch {
      // Non-critical — SW will skip Background Sync if meta is missing
    }

    // Register Background Sync so the SW can retry even if the tab closes
    if ('serviceWorker' in navigator && 'SyncManager' in window) {
      try {
        const reg = await navigator.serviceWorker.ready;
        await reg.sync.register('pending-orders-sync');
      } catch {
        // Background Sync not supported — online handler will sync instead
      }
    }
  }, []);

  const deletePendingOrder = useCallback(async (orderId) => {
    await removePendingOrder(orderId);
    setPendingOrdersCount(prev => Math.max(0, prev - 1));
  }, []);

  const setDemoModeEnabled = useCallback((enabled) => {
    const nextEnabled = Boolean(enabled);
    writePosDemoMode(nextEnabled);
    demoModeEnabledRef.current = nextEnabled;
    setDemoModeEnabledState(nextEnabled);
    appendSupportLogEntry({
      type: 'demo',
      message: nextEnabled ? 'Demo-Modus aktiviert.' : 'Demo-Modus deaktiviert.',
      details: { enabled: nextEnabled },
    });
  }, []);

  const value = {
    isOnline,
    isSyncing,
    lastSyncTime,
    demoModeEnabled,
    offlineData,
    pendingOrdersCount,
    syncData,
    setDemoModeEnabled,
    savePendingOrder,
    deletePendingOrder,
    loadOfflineData
  };

  return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>;
}

export function useOffline() {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within an OfflineProvider');
  }
  return context;
}
